import {
  Sparkles,
  ArrowLeftRight,
  Filter,
  Table,
  FileSpreadsheet,
  BarChart3,
  Hash,
  Archive,
  GitCompare,
  PieChart,
  Files,
} from 'lucide-react';
import type { Tool } from '@/registry/types';
import { CsvClean } from './CsvClean';
import { CsvJsonConvert } from './CsvJsonConvert';
import { CsvDedupe } from './CsvDedupe';
import { CsvViewer } from './CsvViewer';
import { XlsxConvert } from './XlsxConvert';
import { CsvPivot } from './CsvPivot';
import { FileChecksum } from './FileChecksum';
import { ZipTool } from './ZipTool';
import { SpreadsheetDiff } from './SpreadsheetDiff';
import { QuickChart } from './QuickChart';
import { DuplicateFinder } from './DuplicateFinder';

export const dataTools: Tool[] = [
  {
    id: 'csv-clean',
    name: 'CSV cleaner',
    category: 'data',
    description: 'Trim, normalize headers, drop blanks, dedupe rows.',
    keywords: ['csv', 'clean', 'tidy', 'whitespace', 'header'],
    icon: Sparkles,
    component: CsvClean,
    featured: true,
    accepts: 'csv',
    inBrowser: true,
  },
  {
    id: 'xlsx-convert',
    name: 'Excel ↔ CSV ↔ JSON',
    category: 'data',
    description: 'Open .xlsx/.xls/.csv, switch sheets, export to any format.',
    keywords: ['excel', 'xlsx', 'xls', 'csv', 'json', 'spreadsheet', 'sheet'],
    icon: FileSpreadsheet,
    component: XlsxConvert,
    featured: true,
    accepts: 'csv',
    inBrowser: true,
  },
  {
    id: 'spreadsheet-diff',
    name: 'Spreadsheet diff',
    category: 'data',
    description: 'Compare two CSV or Excel files cell-by-cell with redlines.',
    keywords: ['diff', 'compare', 'spreadsheet', 'csv', 'excel', 'xlsx'],
    icon: GitCompare,
    component: SpreadsheetDiff,
    badge: 'new',
    inBrowser: true,
  },
  {
    id: 'quick-chart',
    name: 'Quick chart',
    category: 'data',
    description: 'Bar, line, and pie charts from CSV. Export PNG or SVG.',
    keywords: ['chart', 'graph', 'visualize', 'csv', 'bar', 'line', 'pie'],
    icon: PieChart,
    component: QuickChart,
    featured: true,
    accepts: 'csv',
    badge: 'new',
    inBrowser: true,
  },
  {
    id: 'csv-to-json',
    name: 'CSV ↔ JSON',
    category: 'data',
    description: 'Bidirectional CSV and JSON conversion with validation.',
    keywords: ['csv', 'json', 'convert', 'transform'],
    icon: ArrowLeftRight,
    component: CsvJsonConvert,
    accepts: 'csv',
    inBrowser: true,
  },
  {
    id: 'csv-dedupe',
    name: 'CSV deduplicator',
    category: 'data',
    description: 'Remove duplicates by selected columns.',
    keywords: ['csv', 'duplicate', 'unique', 'dedupe', 'distinct'],
    icon: Filter,
    component: CsvDedupe,
    accepts: 'csv',
    inBrowser: true,
  },
  {
    id: 'csv-pivot',
    name: 'CSV pivot table',
    category: 'data',
    description: 'Cross-tab any CSV with row, column, value, and aggregator.',
    keywords: ['pivot', 'cross-tab', 'aggregate', 'sum', 'csv', 'analytics'],
    icon: BarChart3,
    component: CsvPivot,
    accepts: 'csv',
    inBrowser: true,
  },
  {
    id: 'csv-viewer',
    name: 'CSV viewer',
    category: 'data',
    description: 'Sort, filter, and export any CSV.',
    keywords: ['csv', 'table', 'view', 'sort', 'filter'],
    icon: Table,
    component: CsvViewer,
    accepts: 'csv',
    inBrowser: true,
  },
  {
    id: 'file-checksum',
    name: 'File checksum',
    category: 'data',
    description: 'SHA-1/256/384/512 of any file. Verify against expected hash.',
    keywords: ['checksum', 'hash', 'sha', 'verify', 'file', 'integrity'],
    icon: Hash,
    component: FileChecksum,
    inBrowser: true,
  },
  {
    id: 'duplicate-finder',
    name: 'Duplicate file finder',
    category: 'data',
    description: 'Hash files and group identical content — see what is wasting space.',
    keywords: ['duplicate', 'identical', 'dedupe', 'files', 'hash', 'wasted'],
    icon: Files,
    component: DuplicateFinder,
    badge: 'new',
    inBrowser: true,
  },
  {
    id: 'zip-tool',
    name: 'ZIP archives',
    category: 'data',
    description: 'Pack files into a ZIP, or extract any ZIP.',
    keywords: ['zip', 'archive', 'compress', 'extract', 'unzip'],
    icon: Archive,
    component: ZipTool,
    inBrowser: true,
  },
];

export const jsonToCsvAlias = dataTools[4];
